import React, { useMemo, useRef, useState } from "react";
import { useQuery } from "@apollo/client";
import { GET_User_For_Account } from "../../services/userAPI";
import { AgGridReact } from "ag-grid-react";
import RoleAddUsersModal from "./RoleAddUsersModal";
import BannerIllustration from "../../Images/welcomeBackDashboardIcon.png";

const RoleUserManagement = ({ roleId, accountId, onViewUser }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const gridApiRef = useRef(null);

  const { data, loading, refetch } = useQuery(GET_User_For_Account, {
    variables: { roleId: parseInt(roleId), pagingCriteria: { limit: 10000 } },
    skip: !roleId,
    fetchPolicy: "network-only",
  });

  const users = data?.getUsersForAccount?.data || [];
  const totalCount = data?.getUsersForAccount?.pageMeta?.totalCount ?? users.length;

  const tableData = useMemo(
    () =>
      users.map((user) => ({
        UserId: user.id || null,
        employeeId: user.employeeId || "",
        name: user.fullname || user.name || "",
        email: user.email || "",
        phoneNumber: user.phoneNumber || "",
        designation: user.designation || "",
        joiningDate: user.joiningDate || "",
        profilePicture: user.profilePicture || "",
        raw: user,
      })),
    [users]
  );

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Employee ID",
        headerTooltip: "Employee ID",
        field: "employeeId",
        sortable: true,
        filter: "agTextColumnFilter",
        floatingFilter: true,
      },
      {
        headerName: "Name",
        headerTooltip: "Name",
        field: "name",
        sortable: true,
        filter: "agTextColumnFilter",
        floatingFilter: true,
        cellRenderer: (params) => (
          <span
            style={{ cursor: "pointer" }}
            onClick={() => onViewUser?.(params.data.raw)}
          >
            {params.value}
          </span>
        ),
      },
      {
        headerName: "Email",
        headerTooltip: "Email",
        field: "email",
        sortable: true,
        filter: "agTextColumnFilter",
        floatingFilter: true,
      },
      {
        headerName: "Phone",
        headerTooltip: "Phone",
        field: "phoneNumber",
        sortable: true,
        filter: "agTextColumnFilter",
        floatingFilter: true,
      },
      {
        headerName: "Designation",
        headerTooltip: "Designation",
        field: "designation",
        sortable: true,
        filter: "agTextColumnFilter",
        floatingFilter: true,
      },
      {
        headerName: "Date of Joining",
        headerTooltip: "Date of Joining",
        field: "joiningDate",
        sortable: true,
        filter: "agTextColumnFilter",
        floatingFilter: true,
        valueGetter: (params) => {
          if (!params.data?.joiningDate) return "";
          const date = new Date(params.data.joiningDate);
          if (isNaN(date)) return params.data.joiningDate;
          const day = String(date.getDate()).padStart(2, "0");
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const year = date.getFullYear();
          return `${day}/${month}/${year}`;
        },
      },
    ],
    [onViewUser]
  );

  const defaultColDef = useMemo(
    () => ({
      flex: 1,
      minWidth: 100,
      resizable: true,
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellStyle: { padding: "10px" },
      tooltipValueGetter: (params) => params.value,
      suppressMenu: false,
    }),
    []
  );

  return (
    <div className="flex w-full flex-col gap-5">
      {/* Banner */}
      <div className="flex items-center justify-between rounded-[12px] border border-[#F0D9D9] bg-gradient-to-r from-[#FBEAEA] to-[#FDF6F6] px-6 py-5">
        <div>
          <h2 className="text-[32px] font-bold leading-[28px] tracking-[0.6px] text-[#1F2020]">
            User Management
          </h2>
          <p className="mt-2 max-w-[547px] text-[16px] font-medium leading-[24px] text-[#888888]">
            View user information, manage account details, assign roles, and monitor activity from one place.
          </p>
          <button
            type="button"
            onClick={() => setShowAddModal(true)}
            className="mt-3 flex h-[36px] items-center rounded-[6px] bg-[#AD0101] px-4 text-[13px] font-medium text-white transition-colors hover:bg-[#8C0101]"
          >
            Add Users
          </button>
        </div>
        <img
          src={BannerIllustration}
          alt="User Management"
          className="hidden h-[90px] object-contain md:block"
        />
      </div>

      {/* AG Grid table */}
      <div
        className="ag-grid-wrapper"
        style={{ width: "100%", height: "480px", overflowX: "hidden", position: "relative" }}
      >
        {loading && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(255, 255, 255, 0.7)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 10,
            }}
          >
            <div className="spinner-loader"></div>
          </div>
        )}
        <AgGridReact
          rowData={tableData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          pagination={true}
          paginationPageSize={50}
          paginationPageSizeSelector={[50, 100]}
          onGridReady={(params) => {
            gridApiRef.current = params.api;
          }}
          onRowClicked={(params) => onViewUser?.(params.data.raw)}
          className="user-management-ag-grid"
          suppressCellFocus={true}
          animateRows={false}
          suppressHorizontalScroll={true}
          tooltipShowDelay={0}
          rowBuffer={10}
          suppressColumnVirtualisation={false}
          debounceVerticalScrollbar={true}
        />
      </div>

      {/* Footer */}
      <div className="text-[13px] text-[#1F2020]">Total users- {totalCount}</div>

      <RoleAddUsersModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        roleId={roleId}
        accountId={accountId}
        onSaved={() => {
          setShowAddModal(false);
          refetch();
        }}
      />
    </div>
  );
};

export default RoleUserManagement;
